import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Calculator, CheckCircle2, Plus } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { KpiPeriodos } from "@/componentes/kpi/KpiNomina"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"
import { PagoPuntual } from "@/paginas/interno/nomina/PagoPuntual"
import {
  FRECUENCIAS,
  type Frecuencia,
  type Periodo,
  type PeriodoResumen,
  TONO_ESTADO,
} from "@/paginas/interno/nomina/tipos"

/**
 * Las corridas de nómina: quién cobra qué este período.
 *
 * ⚠ **Aprobar NO paga.** Congela los recibos y redacta una orden de pago en BORRADOR
 * por empleado; el dinero sale después, desde Tesorería y con las tres firmas. Un
 * módulo que pagara por su cuenta sería un segundo camino al banco.
 *
 * ⚠ **Sólo se recalcula un borrador.** Aprobado ya redactó sus órdenes: recalcularlo
 * dejaría el recibo diciendo una cifra y la orden otra.
 */
export function Periodos() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [creando, setCreando] = useState(false)
  const [abierto, setAbierto] = useState<number | null>(null)
  const puedeProcesar = tienePermiso(usuario, "nomina:procesar")
  const vista = useRef<HTMLDivElement | null>(null)

  const periodos = useQuery({
    queryKey: ["nomina-periodos"],
    queryFn: () => api.get<PeriodoResumen[]>("/api/v1/nomina/periodos"),
  })

  const crear = useMutation({
    meta: { exito: "Período creado" },
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Periodo>("/api/v1/nomina/periodos", cuerpo),
    onSuccess: (p) => {
      clienteQuery.invalidateQueries({ queryKey: ["nomina-periodos"] })
      setCreando(false)
      setAbierto(p.id)
    },
  })

  if (periodos.isPending) return <p className="py-6">Cargando…</p>
  if (periodos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los períodos.
      </p>
    )
  }

  const filas = periodos.data

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-end gap-2">
        <Exportar titulo="Períodos de nómina" contenido={vista} />
        {puedeProcesar && (
          <PagoPuntual
            onCreada={() => clienteQuery.invalidateQueries({ queryKey: ["nomina-periodos"] })}
          />
        )}
        {puedeProcesar && (
          <Boton variante="primario" onClick={() => setCreando(true)}>
            <Plus size={16} /> Abrir período
          </Boton>
        )}
      </div>
      <KpiPeriodos />

      <div ref={vista}>
        <Tabla etiqueta="Períodos de nómina">
          <Cabecera>
            <Th>Código</Th>
            <Th>Frecuencia</Th>
            <Th>Desde</Th>
            <Th>Hasta</Th>
            <Th>Se paga</Th>
            <Th>Estado</Th>
          </Cabecera>
          <Cuerpo>
            {filas.length === 0 && (
              <TablaVacia colSpan={6}>
                Todavía no se ha corrido ninguna nómina. Abre un período para calcularla.
              </TablaVacia>
            )}
            {filas.map((p) => (
              <Fila key={p.id} className="cursor-pointer" onClick={() => setAbierto(p.id)}>
                <Td>{p.codigo}</Td>
                <Td>
                  {FRECUENCIAS.find((f) => f.valor === p.frecuencia)?.etiqueta ?? p.frecuencia}
                </Td>
                <Td>{comoFecha(p.desde)}</Td>
                <Td>{comoFecha(p.hasta)}</Td>
                <Td>{comoFecha(p.fecha_pago)}</Td>
                <Td>
                  <Insignia tono={TONO_ESTADO[p.estado]}>{p.estado.toLowerCase()}</Insignia>
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      {abierto !== null && <FichaPeriodo periodoId={abierto} onCerrar={() => setAbierto(null)} />}

      <Modal
        abierto={creando}
        titulo="Abrir un período de nómina"
        descripcion="Se calculan los recibos de los contratos vivos de esa frecuencia."
        onCerrar={() => setCreando(false)}
      >
        {creando && (
          <FormularioPeriodo
            enviando={crear.isPending}
            error={crear.error instanceof ErrorApi ? crear.error.message : null}
            onEnviar={(cuerpo) => crear.mutate(cuerpo)}
            onCancelar={() => setCreando(false)}
          />
        )}
      </Modal>
    </section>
  )
}

/**
 * Los recibos de un período como documento exportable: en pantalla las cifras van crudas
 * y el empleado lleva debajo sus líneas, así que se arma con los datos y no con la tabla.
 */
function documentoRecibos(periodo: Periodo): DocumentoExportable {
  const n = (t: string) => Number(t)
  return {
    titulo: `Nómina ${periodo.codigo}`,
    subtitulo: `Del ${comoFecha(periodo.desde)} al ${comoFecha(periodo.hasta)} · se paga el ${comoFecha(periodo.fecha_pago)} · ${periodo.estado.toLowerCase()}`,
    secciones: [
      {
        titulo: "Recibos del período",
        columnas: [
          { titulo: "Empleado", tipo: "texto" },
          { titulo: "Conceptos", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Sueldo", tipo: "monto" },
          { titulo: "Asignaciones", tipo: "monto" },
          { titulo: "Deducciones", tipo: "monto" },
          { titulo: "Neto", tipo: "monto" },
          { titulo: "Orden", tipo: "texto" },
        ],
        filas: periodo.recibos.map((r) => ({
          celdas: [
            r.persona ?? null,
            r.lineas
              .map((l) => `${l.tipo === "DEDUCCION" ? "−" : "+"}${l.monto} ${l.descripcion}`)
              .join(" · ") || null,
            r.divisa ?? null,
            n(r.sueldo_base),
            n(r.total_asignaciones),
            n(r.total_deducciones),
            n(r.neto),
            r.orden_id === null ? null : `#${r.orden_id}`,
          ],
        })),
      },
    ],
  }
}

function FichaPeriodo({ periodoId, onCerrar }: { periodoId: number; onCerrar: () => void }) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const puedeProcesar = tienePermiso(usuario, "nomina:procesar")

  const consulta = useQuery({
    queryKey: ["nomina-periodo", periodoId],
    queryFn: () => api.get<Periodo>(`/api/v1/nomina/periodos/${periodoId}`),
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["nomina-periodo", periodoId] })
    clienteQuery.invalidateQueries({ queryKey: ["nomina-periodos"] })
    clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
  }

  const recalcular = useMutation({
    meta: { exito: "Recibos recalculados" },
    mutationFn: () => api.post(`/api/v1/nomina/periodos/${periodoId}/recalcular`, {}),
    onSuccess: refrescar,
  })
  const aprobar = useMutation({
    meta: {
      exito: { titulo: "Período aprobado", texto: "Sus órdenes se redactaron en borrador." },
    },
    mutationFn: () => api.post(`/api/v1/nomina/periodos/${periodoId}/aprobar`, {}),
    onSuccess: refrescar,
  })

  const periodo = consulta.data
  const enBorrador = periodo?.estado === "BORRADOR"
  const error =
    aprobar.error instanceof ErrorApi
      ? aprobar.error.message
      : recalcular.error instanceof ErrorApi
        ? recalcular.error.message
        : null

  return (
    <Cajon abierto titulo={periodo?.codigo ?? "Período"} onCerrar={onCerrar} ancho="amplio">
      {consulta.isPending && <p>Cargando…</p>}
      {periodo && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {comoFecha(periodo.desde)} a {comoFecha(periodo.hasta)} · se paga el{" "}
              {comoFecha(periodo.fecha_pago)} ·{" "}
              <Insignia tono={TONO_ESTADO[periodo.estado]}>{periodo.estado.toLowerCase()}</Insignia>
            </p>
            <Exportar
              titulo={`Nómina ${periodo.codigo}`}
              documento={() => documentoRecibos(periodo)}
            />
          </div>

          {error && (
            <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          <Tabla etiqueta="Recibos del período">
            <Cabecera>
              <Th>Empleado</Th>
              <Th derecha>Sueldo</Th>
              <Th derecha>Asignaciones</Th>
              <Th derecha>Deducciones</Th>
              <Th derecha>Neto</Th>
              <Th>Orden</Th>
            </Cabecera>
            <Cuerpo>
              {periodo.recibos.length === 0 && (
                <TablaVacia colSpan={6}>
                  Sin recibos: no hay contratos vivos de esa frecuencia.
                </TablaVacia>
              )}
              {periodo.recibos.map((r) => (
                <Fila key={r.id}>
                  <Td>
                    <span className="block">{r.persona ?? "—"}</span>
                    {r.lineas.length > 0 && (
                      <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                        {r.lineas
                          .map(
                            (l) =>
                              `${l.tipo === "DEDUCCION" ? "−" : "+"}${l.monto} ${l.descripcion}`,
                          )
                          .join(" · ")}
                      </span>
                    )}
                  </Td>
                  <Td derecha numero>
                    {r.sueldo_base}
                  </Td>
                  <Td derecha numero>
                    {r.total_asignaciones}
                  </Td>
                  <Td derecha numero>
                    {r.total_deducciones}
                  </Td>
                  <Td derecha numero>
                    <strong>
                      {r.neto} {r.divisa ?? ""}
                    </strong>
                  </Td>
                  <Td>{r.orden_id === null ? "—" : `#${r.orden_id}`}</Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>

          {puedeProcesar && enBorrador && (
            <div className="flex flex-wrap justify-end gap-2">
              <Boton
                variante="secundario"
                onClick={() => recalcular.mutate()}
                disabled={recalcular.isPending}
              >
                <Calculator size={16} /> Recalcular
              </Boton>
              <Boton
                variante="primario"
                onClick={() => aprobar.mutate()}
                disabled={aprobar.isPending || periodo.recibos.length === 0}
              >
                <CheckCircle2 size={16} /> Aprobar y redactar las órdenes
              </Boton>
            </div>
          )}
          {enBorrador && (
            <p className="text-right text-xs" style={{ color: "var(--fg-muted)" }}>
              Aprobar no paga: redacta una orden en borrador por empleado, que sigue el circuito de
              siempre.
            </p>
          )}
        </div>
      )}
    </Cajon>
  )
}

function FormularioPeriodo({
  enviando,
  error,
  onEnviar,
  onCancelar,
}: {
  enviando: boolean
  error: string | null
  onEnviar: (cuerpo: Record<string, unknown>) => void
  onCancelar: () => void
}) {
  const [frecuencia, setFrecuencia] = useState<Frecuencia>("QUINCENAL")

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        const datos = new FormData(e.currentTarget)
        const texto = (campo: string) => String(datos.get(campo) ?? "").trim()
        onEnviar({
          frecuencia,
          desde: texto("desde"),
          hasta: texto("hasta"),
          fecha_pago: texto("fecha_pago"),
        })
      }}
    >
      <Campo para="pn-frecuencia" etiqueta="Frecuencia">
        <select
          id="pn-frecuencia"
          value={frecuencia}
          onChange={(e) => setFrecuencia(e.target.value as Frecuencia)}
          className="campo mt-1"
        >
          {FRECUENCIAS.map((f) => (
            <option key={f.valor} value={f.valor}>
              {f.etiqueta}
            </option>
          ))}
        </select>
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="pn-desde" etiqueta="Desde">
          <input id="pn-desde" name="desde" type="date" required className="campo mt-1" />
        </Campo>
        <Campo para="pn-hasta" etiqueta="Hasta">
          <input id="pn-hasta" name="hasta" type="date" required className="campo mt-1" />
        </Campo>
      </div>

      <Campo
        para="pn-pago"
        etiqueta="Fecha de pago"
        ayuda="La que heredan las órdenes como fecha requerida."
      >
        <input id="pn-pago" name="fecha_pago" type="date" required className="campo mt-1" />
      </Campo>

      {error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" variante="primario" disabled={enviando}>
          Abrir y calcular
        </Boton>
      </div>
    </form>
  )
}
